import { useAuth0 } from '@auth0/auth0-react';
import { milliseconds } from 'date-fns';
import { getIdToken, onAuthStateChanged, onIdTokenChanged, signInAnonymously, signOut } from 'firebase/auth';
import { createContext, useContext, useEffect, useState } from 'react';

import { useNotificationContext } from 'core/context/Notifications';
import { fbAuth } from 'support/auth/firebase';

import { Auth0Provider } from './auth0';

/**
 * Interval to check whether a token needs refreshing.
 *
 * Both firebase and auth0 will refresh the token
 * if they will expire within 5 minutes after checking
 */
export const REFRESH_TOKEN_INTERVAL = milliseconds({ minutes: 1 });

export interface AuthTokenContextType {
  /**
   * Auth token to send to backend
   */
  token?: string;

  /**
   * Identifier unique to a given user across multiple tokens
   */
  tokenID?: string;

  /**
   * Whether this is a passwordless login
   */
  isSSO?: boolean;

  /**
   * Logout the user from the auth provider
   */
  logout(): Promise<void>;
}

export const AuthTokenContext = createContext<AuthTokenContextType>({ async logout() {} });
export function useAuthTokenContext() {
  return useContext(AuthTokenContext);
}

export function AuthTokenContextProvider({ children }: { children: React.ReactNode }) {
  return (
    <Auth0Provider>
      <TokenResolver>
        {value => <AuthTokenContext.Provider value={value}>{children}</AuthTokenContext.Provider>}
      </TokenResolver>
    </Auth0Provider>
  );
}

interface TokenResolverProps {
  children(props: AuthTokenContextType): JSX.Element;
}

function TokenResolver({ children }: TokenResolverProps) {
  const auth0 = useAuth0();

  function saveTokenCookie(value: AuthTokenContextType) {
    const cookieDomain = document.location.hostname.split('.').splice(-2).join('.');

    if (value.token) {
      document.cookie = `token=${value.token};path=/;domain=${cookieDomain};Secure;SameSite=None`;
    } else {
      document.cookie = `token=;path=/;domain=${cookieDomain};expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
    }

    return children(value);
  }

  return auth0.isLoading || auth0.isAuthenticated ? (
    <Auth0TokenResolver>{saveTokenCookie}</Auth0TokenResolver>
  ) : (
    <FirebaseTokenResolver>{saveTokenCookie}</FirebaseTokenResolver>
  );
}

function Auth0TokenResolver({ children }: TokenResolverProps) {
  const [token, setToken] = useState<string>();
  const { dispatch } = useNotificationContext();
  const auth0 = useAuth0();

  useEffect(() => {
    if (auth0.error) dispatch('error', 'Auth error', auth0.error.message);
    if (!auth0.isAuthenticated) return;

    // auth0 reference changes everytime token is updated, here we save cached version
    auth0
      .getAccessTokenSilently({ cacheMode: 'cache-only' })
      .then(setToken)
      .catch(error => {
        dispatch('error', 'Set token error', (error as Error)?.message || String(error));
      });

    // Refresh token
    const refreshIntervalId = window.setInterval(async () => {
      try {
        await auth0.getAccessTokenSilently();
      } catch (error) {
        dispatch('error', 'Refresh token error', (error as Error)?.message || String(error));
      }
    }, REFRESH_TOKEN_INTERVAL);

    return () => {
      window.clearInterval(refreshIntervalId);
    };
  }, [auth0, dispatch]);

  return children({
    token,
    tokenID: token ? auth0.user?.sub : undefined, // Token required to prevent having an ID without a token getting set
    isSSO: true,
    logout: () => auth0.logout({ openUrl: false }),
  });
}

function FirebaseTokenResolver({ children }: TokenResolverProps) {
  const [token, setToken] = useState<string>();
  const { dispatch } = useNotificationContext();

  useEffect(() => {
    // Sign in anonymously if required
    const unsubAuthStatechanged = onAuthStateChanged(fbAuth, async user => {
      if (user) return;

      try {
        await signInAnonymously(fbAuth);
      } catch (error) {
        dispatch('error', 'Anonymous signin error', (error as Error)?.message || String(error));
      }
    });

    // Subscribe to token changes
    const unsubIdTokenChanged = onIdTokenChanged(fbAuth, async user => {
      try {
        setToken(user ? await getIdToken(user) : undefined);
      } catch (error) {
        dispatch('error', 'Set token error', (error as Error)?.message || String(error));
      }
    });

    // Refresh token
    const refreshIntervalId = window.setInterval(async () => {
      const { currentUser } = fbAuth;
      if (!currentUser) return;

      try {
        await getIdToken(currentUser);
      } catch (error) {
        // This operation fails if the user's credentials expired. This error can safely be ignored, the user will be
        // signed out of Firebase.
      }
    }, REFRESH_TOKEN_INTERVAL);

    return () => {
      unsubIdTokenChanged();
      unsubAuthStatechanged();
      window.clearInterval(refreshIntervalId);
    };
  }, [dispatch]);

  return children({
    token,
    tokenID: token ? fbAuth.currentUser?.uid : undefined, // Token required to prevent having an ID without a token getting set
    isSSO: !fbAuth.currentUser?.providerData.some(p => p.providerId === 'password'),
    logout: () => signOut(fbAuth),
  });
}
